import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, ConfirmDialog, ErrorNote, Field, Toggle, inputClass } from '../components/ui';
import { errorMessage } from '../lib/api';
import {
  customFields,
  imports,
  listPipelines,
  listStages,
  products as productsApi,
  tags as tagsApi,
} from '../lib/resources';
import type { ImportPreview } from '../types';

/** Destinos do mapeamento (desenho nosso, decisão Q13). */
const TARGETS: Array<{ group: string; items: Array<[string, string]> }> = [
  {
    group: 'Lead',
    items: [
      ['name', 'Nome / Razão social'],
      ['kind', 'Tipo (Pessoa/Empresa)'],
      ['displayName', 'Nome de exibição'],
      ['tradeName', 'Nome fantasia'],
      ['phone', 'Telefone'],
      ['email', 'E-mail'],
      ['source', 'Origem'],
      ['taxId', 'Documento (CPF/CNPJ)'],
      ['site', 'Site'],
      ['instagram', 'Instagram'],
      ['birthDate', 'Data de nascimento'],
      ['jobTitle', 'Cargo'],
      ['segment', 'Segmento'],
      ['foundationDate', 'Data de fundação'],
      ['notes', 'Notas'],
      ['company', 'Empresa (texto)'],
      ['tags', 'Tags (separadas por vírgula)'],
      ['lists', 'Listas (separadas por vírgula)'],
      ['attendant', 'Atendente (e-mail)'],
    ],
  },
  {
    group: 'Endereço',
    items: [
      ['address.zip', 'CEP'],
      ['address.address', 'Endereço'],
      ['address.number', 'Número'],
      ['address.complement', 'Complemento'],
      ['address.block', 'Bairro'],
      ['address.city', 'Cidade'],
      ['address.state', 'UF'],
      ['address.country', 'País'],
    ],
  },
  {
    group: 'Negócio',
    items: [
      ['business.pipeline', 'Pipeline'],
      ['business.stage', 'Etapa'],
      ['business.product', 'Produto ou SKU'],
      ['business.value', 'Valor'],
    ],
  },
];

/**
 * Importação de leads/negócios (doc 02, 2) — página inteira: arquivo, opções,
 * tags e negócio para todos, mapeamento de colunas e criação da execução.
 */
export function LeadImportPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [hasHeader, setHasHeader] = useState(true);
  const [delimiter, setDelimiter] = useState<'auto' | ',' | ';'>('auto');
  const [updateDuplicates, setUpdateDuplicates] = useState(false);
  const [defaultKind, setDefaultKind] = useState<'PERSON' | 'COMPANY'>('PERSON');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [productId, setProductId] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [confirming, setConfirming] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const tags = useQuery({ queryKey: ['tags'], queryFn: () => tagsApi.list() });
  const pipelines = useQuery({ queryKey: ['pipelines'], queryFn: listPipelines });
  const stages = useQuery({
    queryKey: ['stages', pipelineId],
    queryFn: () => listStages(pipelineId),
    enabled: Boolean(pipelineId),
  });
  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list({ take: 200 }),
  });
  const fields = useQuery({
    queryKey: ['custom-fields', 'import'],
    queryFn: () => customFields.list(),
  });

  const read = useMutation({
    mutationFn: (selected: File) => imports.preview(selected, delimiter),
    onSuccess: (result) => {
      setPreview(result);
      setMapping(result.suggestions);
    },
  });
  const start = useMutation({
    mutationFn: () =>
      imports.start(file!, {
        hasHeader,
        delimiter,
        updateDuplicates,
        defaultKind,
        tagIds,
        pipelineId: pipelineId || undefined,
        stageId: stageId || undefined,
        productId: productId || undefined,
        mapping: Object.fromEntries(
          Object.entries(mapping).filter(([, target]) => target && target !== 'ignore'),
        ),
      }),
    onSuccess: () => navigate('/configuracoes/execucoes'),
  });

  const fieldTargets = (fields.data?.data ?? [])
    .filter((field) => field.entity !== 'business')
    .map((field) => [`field:${field.id}`, field.name] as [string, string]);
  const columns = preview
    ? hasHeader
      ? preview.headers
      : preview.headers.map((_, index) => `Coluna ${index + 1}`)
    : [];
  const samples = preview
    ? hasHeader
      ? preview.rows
      : [preview.headers, ...preview.rows]
    : [];
  const rows = preview ? preview.totalRows + (hasHeader ? 0 : 1) : 0;
  const nameMapped = Object.values(mapping).includes('name');

  return (
    <div className="mx-auto max-w-5xl space-y-5 p-8">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Importar leads/negócios</h1>
          <p className="text-sm text-slate-500">
            Envie um CSV, confira uma amostra e crie uma execução para processar o arquivo.
          </p>
        </div>
        <Link to="/leads">
          <Button variant="ghost">Voltar aos leads</Button>
        </Link>
      </header>

      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-sm font-semibold text-slate-900">Arquivo CSV</h2>
        <p className="mt-1 text-xs text-slate-500">
          CSV em UTF-8, até 8 MB. A tela lê só uma amostra para a pré-visualização.
        </p>
        <input
          type="file"
          accept=".csv,text/csv"
          className="mt-3 text-sm"
          onChange={(event) => {
            const selected = event.target.files?.[0] ?? null;
            setFile(selected);
            setPreview(null);
            if (selected) read.mutate(selected);
          }}
        />
        {read.error && (
          <div className="mt-2">
            <ErrorNote>{errorMessage(read.error)}</ErrorNote>
          </div>
        )}
      </section>

      <section className="grid gap-5 md:grid-cols-2">
        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">Opções do CSV</h2>
          <Toggle label="Arquivo tem cabeçalho" checked={hasHeader} onChange={setHasHeader} />
          <Field label="Delimitador">
            <select
              className={inputClass}
              value={delimiter}
              onChange={(event) => {
                const value = event.target.value as typeof delimiter;
                setDelimiter(value);
                if (file) read.mutate(file);
              }}
            >
              <option value="auto">Detectar automaticamente</option>
              <option value=",">Vírgula (,)</option>
              <option value=";">Ponto e vírgula (;)</option>
            </select>
          </Field>
          <Field label="Tipo dos leads novos" hint="Quando a planilha não tem a coluna Tipo.">
            <select
              className={inputClass}
              value={defaultKind}
              onChange={(event) => setDefaultKind(event.target.value as 'PERSON' | 'COMPANY')}
            >
              <option value="PERSON">Pessoa</option>
              <option value="COMPANY">Empresa</option>
            </select>
          </Field>
          <Toggle
            label="Atualizar todos os leads duplicados correspondentes"
            hint="Duplicado = mesmo telefone ou e-mail."
            checked={updateDuplicates}
            onChange={setUpdateDuplicates}
          />
          <button
            type="button"
            className="text-xs text-sky-700 underline"
            onClick={() => setShowHelp(!showHelp)}
          >
            Como funciona
          </button>
          {showHelp && (
            <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
              <p>
                1. A linha corresponde a um lead pelo telefone OU pelo e-mail. Se a conta já tem
                duplicados, ela corresponde a todos.
              </p>
              <p className="mt-1">
                2. Desligado (padrão): a linha não é importada, vira conflito e os leads
                correspondentes vão para o arquivo "Leads duplicados".
              </p>
              <p className="mt-1">
                3. Ligado: os dados da linha — inclusive negócio, tags e campos adicionais — são
                aplicados a todos os correspondentes.
              </p>
            </div>
          )}
        </div>

        <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">Para todos os leads</h2>
          <div>
            <p className="text-sm font-medium text-slate-700">Tags</p>
            <div className="mt-1 flex max-h-28 flex-wrap gap-1 overflow-y-auto">
              {(tags.data?.data ?? []).map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() =>
                    setTagIds((current) =>
                      current.includes(tag.id)
                        ? current.filter((id) => id !== tag.id)
                        : [...current, tag.id],
                    )
                  }
                  className={`rounded-full px-2 py-0.5 text-xs ${tagIds.includes(tag.id) ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600'}`}
                >
                  {tag.name}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-slate-500">
            Negócio para todos: criado para cada lead importado que não tiver pipeline e etapa
            nas colunas.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <select
              className={`${inputClass} mt-0`}
              value={pipelineId}
              onChange={(event) => {
                setPipelineId(event.target.value);
                setStageId('');
              }}
            >
              <option value="">Pipeline…</option>
              {(pipelines.data?.data ?? []).map((pipeline) => (
                <option key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </option>
              ))}
            </select>
            <select
              className={`${inputClass} mt-0`}
              value={stageId}
              disabled={!pipelineId}
              onChange={(event) => setStageId(event.target.value)}
            >
              <option value="">Etapa…</option>
              {(stages.data ?? []).map((stage) => (
                <option key={stage.id} value={stage.id}>
                  {stage.name}
                </option>
              ))}
            </select>
          </div>
          <select
            className={`${inputClass} mt-0`}
            value={productId}
            disabled={!stageId}
            onChange={(event) => setProductId(event.target.value)}
          >
            <option value="">Produto (opcional)…</option>
            {(products.data?.data ?? []).map((product) => (
              <option key={product.id} value={product.id}>
                {product.name}
              </option>
            ))}
          </select>
        </div>
      </section>

      {preview && (
        <section className="rounded-xl border border-slate-200 bg-white p-5">
          <h2 className="text-sm font-semibold text-slate-900">Mapeamento de colunas</h2>
          <p className="mt-1 text-xs text-slate-500">
            Mapeie só as colunas que devem ser importadas. Pipeline, etapa, produto, SKU e tags
            precisam existir no CRM; o vínculo ignora maiúsculas, espaços e acentos. {rows}{' '}
            linha(s) no arquivo.
          </p>
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-400">
              <tr>
                <th className="py-2">Coluna</th>
                <th className="py-2">Amostra</th>
                <th className="py-2">Importar como</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {columns.map((column, index) => (
                <tr key={`${column}-${index}`}>
                  <td className="py-2 font-medium text-slate-700">{column}</td>
                  <td className="max-w-xs truncate py-2 text-xs text-slate-500">
                    {samples
                      .slice(0, 3)
                      .map((row) => row[index])
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </td>
                  <td className="py-2">
                    <select
                      className={`${inputClass} mt-0`}
                      value={mapping[String(index)] ?? 'ignore'}
                      onChange={(event) =>
                        setMapping({ ...mapping, [String(index)]: event.target.value })
                      }
                    >
                      <option value="ignore">Não importar</option>
                      {TARGETS.map((group) => (
                        <optgroup key={group.group} label={group.group}>
                          {group.items.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                      {fieldTargets.length > 0 && (
                        <optgroup label="Campos adicionais">
                          {fieldTargets.map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!nameMapped && (
            <p className="mt-2 text-xs text-amber-700">
              Mapeie a coluna do nome (ou razão social) para continuar.
            </p>
          )}
        </section>
      )}

      {start.error && (
        <ErrorNote>
          {errorMessage(start.error, 'Não foi possível criar a importação')}
        </ErrorNote>
      )}
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          O processamento acontece em segundo plano e pode ser acompanhado em Execuções.
        </p>
        <Button
          variant="primary"
          disabled={!preview || !nameMapped || start.isPending}
          onClick={() => setConfirming(true)}
        >
          Criar importação
        </Button>
      </div>

      {confirming && (
        <ConfirmDialog
          title="Criar importação?"
          message={
            <p>
              {rows} linha(s) serão processadas
              {tagIds.length ? `, com ${tagIds.length} tag(s) para todos` : ''}
              {stageId ? ' e um negócio para cada lead sem pipeline na planilha' : ''}.
              {updateDuplicates
                ? ' Duplicados serão atualizados.'
                : ' Duplicados vão para o arquivo de conflitos.'}
            </p>
          }
          confirmLabel="Importar"
          busy={start.isPending}
          onConfirm={() => start.mutate()}
          onClose={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
